/**
 * Búsqueda de texto en CLIENTE: sin mayúsculas, sin acentos, por palabras.
 *
 * «eric» encuentra «Èric», «alex galan» encuentra «Àlex Mera Galán» y
 * «roura anna» encuentra «Anna Roura»: cada palabra se exige por separado, en
 * cualquier orden, y cada una puede ser parte de una palabra («senyal»
 * encuentra «senyalització»).
 *
 * 🔴 Es la MISMA normalización que usa el backend para `descriptionSearch`
 * (`rois-back/src/utils/description-search.helper.ts` → `normalizeSearchText`):
 * NFD + quitar las marcas combinantes, minúsculas, y cualquier carácter que no
 * sea `[a-z0-9]` convertido en separador. Así un desplegable (que filtra aquí)
 * y una tabla (que filtra en el servidor) encuentran lo mismo con lo mismo. Si
 * se cambia una, se cambia la otra.
 *
 * Por qué NFD y no `Intl.Collator({ sensitivity: 'base' })`: el Collator
 * compara cadenas enteras (sirve para ORDENAR), no busca subcadenas. Para
 * filtrar, lo estándar es normalizar los dos lados y buscar con `includes`.
 * La «ñ» queda como «n» y la «ç» como «c» — igual que en el backend, y es lo que
 * espera quien teclea sin la tecla a mano (tablet).
 */

/** Forma canónica de búsqueda. `''` si no queda nada útil. */
export function normalizeSearchText(input: string | null | undefined): string {
  if (typeof input !== 'string' || input.length === 0) return '';
  return (
    input
      .normalize('NFD')
      // «Combining diacritical marks»: «ó» → «o» + acento combinante (U+0301) → «o».
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
  );
}

/**
 * ¿Encaja `query` en `text`? `text` puede ser varios campos (nombre, código,
 * correo…): basta con que UNO de ellos contenga TODAS las palabras.
 *
 * 🔴 Todas en el MISMO campo, no repartidas entre columnas. Con la unión de
 * campos, «Tramo 1» encontraba también el Tramo 2 de los recorridos
 * predefinidos, porque el «1» salía en los PK de la columna de carreteras
 * («164+000») — medido en navegador. Para buscar nombre y apellidos en
 * cualquier orden, el llamante los pasa JUNTOS como un solo campo.
 *
 * La búsqueda se trocea SOLO por espacios, y cada trozo debe aparecer en el
 * campo (subcadena, en cualquier orden) de una de estas dos formas:
 *  - normalizado tal cual: «roura», «m carme», «c 31»;
 *  - todo junto, sin separadores, contra el campo también junto: así los
 *    códigos y los nombres con símbolos se encuentran con o sin ellos
 *    («ot0000540» → «Ot.0000540.0000.01», «1234abc» → «1234-ABC»,
 *    «collegi» → «Col·legi»).
 *
 * No se trocea por los símbolos: «C-31» partido en «c» y «31» encontraría
 * cualquier cosa con una «c» y un «31» («Carretera 331»).
 *
 * Una búsqueda vacía (o solo símbolos) no filtra nada: todo coincide.
 */
export function matchesSearch(
  text: string | null | undefined | ReadonlyArray<string | null | undefined>,
  query: string | null | undefined
): boolean {
  const pieces = (query ?? '')
    .split(/\s+/)
    .map((piece) => normalizeSearchText(piece))
    .filter(Boolean);
  if (pieces.length === 0) return true;

  const compact = (value: string) => value.replace(/ /g, '');
  const fields = Array.isArray(text) ? text : [text];
  return fields.some((field) => {
    const normalized = normalizeSearchText(field);
    if (!normalized) return false;
    const normalizedCompact = compact(normalized);
    return pieces.every(
      (piece) =>
        normalized.includes(piece) || normalizedCompact.includes(compact(piece))
    );
  });
}
